/**
 * Drawer header: chip, "container #N of M · flag country", unsaved badge,
 * Cancel / Save / close. Save is disabled with its reason as a tooltip. A
 * master config adds "Override all containers" (21/10/2026).
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';
import { Crown, X } from 'lucide-react';

import { Badge, Button, Tooltip } from '@/components/ui';
import { countryToFlagEmoji } from '@/utils/country-flag-emoji';

import { ContainerChip } from '../shared/ContainerChip';
import { PushAllButton } from './master/push/PushAllButton';

interface Props {
  name: string;
  color: string;
  position: string;
  countryName: string;
  dirty: boolean;
  saving: boolean;
  invalidReason: string | null;
  saveLabel?: string;
  /** Phase 14 — a master config gets a crown, not a container chip alone. */
  kind?: 'container' | 'master';
  /** A route-test container wears the radio mast on its chip. */
  routeTest?: boolean;
  /** A new editor seeded from a preset can be saved as it stands. */
  isNew?: boolean;
  onSave: () => void;
  onClose: () => void;
}

export const EditorHeader: FC<Props> = ({
  name,
  color,
  position,
  countryName,
  dirty,
  saving,
  invalidReason,
  saveLabel = 'Save container',
  kind = 'container',
  routeTest = false,
  isNew = false,
  onSave,
  onClose,
}) => (
  <div className="flex items-center gap-3">
    {kind === 'master' ? (
      <span className="inline-flex items-center gap-1 text-sm font-semibold text-primary">
        <Crown className="h-4 w-4" /> Master config
      </span>
    ) : null}
    {name.trim() ? <ContainerChip name={name.trim()} color={color} routeTest={routeTest} /> : null}
    <span className="text-xs text-muted-foreground">
      {position} · {countryToFlagEmoji(countryName)} {countryName}
    </span>
    <span className="grow" />
    {dirty && invalidReason ? (
      <span className="max-w-[260px] truncate text-xs font-medium text-warning duration-300 animate-in fade-in-0" title={invalidReason}>
        {invalidReason} — then save
      </span>
    ) : dirty ? (
      <Badge tone="warning">unsaved changes</Badge>
    ) : null}
    {kind === 'master' ? <PushAllButton /> : null}
    <Button variant="outline" size="sm" onClick={onClose}>
      Cancel
    </Button>
    {invalidReason ? (
      <Tooltip content={invalidReason}>
        <span>
          <Button size="sm" disabled>
            {saveLabel}
          </Button>
        </span>
      </Tooltip>
    ) : (
      <Button size="sm" loading={saving} disabled={!dirty && !isNew} onClick={onSave}>
        {saveLabel}
      </Button>
    )}
    <button
      type="button"
      aria-label="Close editor"
      onClick={onClose}
      className="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
    >
      <X className="h-4 w-4" />
    </button>
  </div>
);
